import file from "./countries.json";

/**
 * How one country's addresses are written and its phones dialled, from the
 * pinned sources `libs/utils/scripts/build-countries.ts` names in `countries.json`
 * (Google's address metadata and libphonenumber). Regenerate with that
 * script; never edit the JSON by hand.
 */
export type CountryData = {
  /** Present where the country's addresses have a postcode. `kind` is what
   *  it is called (Google's `zip_name_type`: postal, zip, eircode, pin);
   *  `pattern` the whole postcode, as Google's `zip` regex gives it. */
  postcode?: { kind: string; required: boolean; pattern?: string; example?: string };
  /** Present where the country's addresses have a region (Google's
   *  `state_name_type`: state, county, province, prefecture, emirate…),
   *  with its regions to choose from where Google lists them, each as
   *  [value, name]: its ISO 3166-2 code where Google gives every one, else
   *  Google's key. */
  region?: { kind: string; required: boolean; options?: [string, string][] };
  /** What its town is called, where it is not a city (post_town, suburb,
   *  district). */
  city?: string;
  /** Its phones: the country calling code, the trunk prefix a national
   *  number is dialled with at home (the UK's 0), how libphonenumber reads
   *  a national number, every length of one, and, for a calling code
   *  several countries share, whether this is the one a number with only
   *  the code is read as. */
  phone?: {
    code: string;
    trunk?: string;
    /** libphonenumber's `nationalPrefixForParsing`: what a national number
     *  may start with that is not part of it, where it is more than the
     *  trunk prefix (Argentina's 0 and mobile 15). */
    parsing?: string;
    /** libphonenumber's `nationalPrefixTransformRule`: what that becomes
     *  ("9$1": an Argentine mobile's 9). */
    transform?: string;
    /** libphonenumber's general `nationalNumberPattern`: a whole national
     *  number, whatever its kind. */
    pattern?: string;
    lengths: number[];
    main?: boolean;
  };
};

export type CountryFile = {
  source: { addresses: string; phones: string };
  countries: Record<string, CountryData>;
};

// Read when asked, not at import: a bundle that never asks drops the file.
const countries = () => (file as unknown as CountryFile).countries;

/** The country's data, or nothing for a code the file does not know. */
export const countryData = (country: string | null | undefined): CountryData | undefined =>
  country ? countries()[country.toUpperCase()] : undefined;

/** A region a country lists, as a form offers it: `value`, as saved, is its
 *  code after the country's ("US-CA", ISO 3166-2's where Google gives every
 *  code), so a region saved in one country is never read as another's
 *  (Spain's Cádiz is "CA" too); `label` its name. */
export type RegionOption = { value: string; label: string };

/** The regions a country's addresses are chosen from, where it lists them. */
export function regionOptions(country: string | null | undefined): RegionOption[] | undefined {
  const options = countryData(country)?.region?.options;
  const code = country?.toUpperCase();
  return options?.map(([region, label]) => ({ value: `${code}-${region}`, label }));
}

/**
 * Whether a saved region is one of the country's: one it lists where it lists
 * them, any typed by hand where it does not (but no other country's listed
 * region), none where its addresses have no region. Any, for a country the
 * file does not know.
 */
export function regionFits(country: string | null | undefined, region: string): boolean {
  const data = countryData(country);
  if (!data) return true;
  if (!data.region) return false;
  const options = regionOptions(country);
  if (options) return options.some((option) => option.value === region);
  const other = /^([A-Z]{2})-/.exec(region)?.[1];
  return !other || !regionOptions(other)?.some((option) => option.value === region);
}

/** Every country with a phone, by calling code: those sharing one, its main
 *  country first. */
export const countriesDialling = (code: string): string[] =>
  Object.entries(countries())
    .filter(([, data]) => data.phone?.code === code)
    .sort(([, a], [, b]) => Number(Boolean(b.phone?.main)) - Number(Boolean(a.phone?.main)))
    .map(([country]) => country);
